import { useCallback, useMemo, useRef, useState } from "react";
import { useMessageComposer } from "../../Messages/hooks/useMessageComposer";
import { buildChatDraftKey } from "../../chat-dock/chatDrafts";
import { MessageGroupMember, MessageGroupMention } from "../messageGroups.types";
import { getMemberFullName, getMemberKey } from "../utils/messageGroupDisplay.utils";

const MENTION_TRIGGER = /(?:^|\s)@([^\s@]*)$/;

interface InsertedMention {
  type: MessageGroupMember["type"];
  uid: string;
  name: string;
}

export const resolveMentionOffsets = (text: string, inserted: InsertedMention[]): MessageGroupMention[] => {
  const consumed: { start: number; end: number }[] = [];
  const mentions: MessageGroupMention[] = [];

  inserted.forEach((mention) => {
    const token = `@${mention.name}`;
    let searchFrom = 0;

    while (searchFrom <= text.length) {
      const start = text.indexOf(token, searchFrom);
      if (start === -1) return;

      const overlaps = consumed.some((range) => start < range.end && start + token.length > range.start);
      if (!overlaps) {
        consumed.push({ start, end: start + token.length });
        mentions.push({ type: mention.type, uid: mention.uid, name: mention.name, start, length: token.length });
        return;
      }

      searchFrom = start + 1;
    }
  });

  return mentions.sort((a, b) => a.start - b.start);
};

export const useMessageGroupComposer = ({
  messagesMutation,
  groupUid,
  companyId,
  members,
  onMessageSent,
}: {
  messagesMutation: any;
  groupUid?: string;
  companyId?: number | string | null;
  members: MessageGroupMember[];
  onMessageSent?: () => void;
}) => {
  const insertedMentionsRef = useRef<InsertedMention[]>([]);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const buildExtraPayload = useCallback((text: string) => {
    const mentions = resolveMentionOffsets(text, insertedMentionsRef.current);
    insertedMentionsRef.current = [];
    return mentions.length ? { mentions } : {};
  }, []);

  const composer = useMessageComposer({
    messagesMutation,
    receiver: { groupUid },
    onMessageSent,
    buildExtraPayload,
    draftKey: buildChatDraftKey({ companyId, scope: "group", id: groupUid }),
  });

  const { message, setMessage } = composer;

  const suggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    const normalized = mentionQuery.trim().toLowerCase();
    const mentionable = members.filter((member) => member.type !== "contact");
    const pool = mentionable.filter((member) => !insertedMentionsRef.current.some((inserted) => getMemberKey(inserted) === getMemberKey(member)));
    const source = pool.length ? pool : mentionable;

    return source
      .filter((member) => (normalized ? getMemberFullName(member).toLowerCase().includes(normalized) : true))
      .slice(0, 6);
  }, [mentionQuery, members]);

  const syncMentionQuery = useCallback((value: string, caret: number) => {
    const beforeCaret = value.slice(0, caret);
    const match = MENTION_TRIGGER.exec(beforeCaret);
    setMentionQuery(match ? match[1] : null);
    setActiveIndex(0);
  }, []);

  const handleChange = useCallback(
    (event: any) => {
      const value = event.target.value;
      setMessage(value);
      syncMentionQuery(value, event.target.selectionStart ?? value.length);
    },
    [setMessage, syncMentionQuery],
  );

  const insertMention = useCallback(
    (member: MessageGroupMember) => {
      const name = getMemberFullName(member) || "Unknown";
      const input = inputRef.current;
      const caret = input?.selectionStart ?? message.length;
      const beforeCaret = message.slice(0, caret);
      const match = MENTION_TRIGGER.exec(beforeCaret);
      if (!match) return;

      const triggerStart = beforeCaret.length - match[0].length + (match[0].startsWith("@") ? 0 : 1);
      const nextValue = `${message.slice(0, triggerStart)}@${name} ${message.slice(caret)}`;

      insertedMentionsRef.current = [
        ...insertedMentionsRef.current.filter((inserted) => getMemberKey(inserted) !== getMemberKey(member)),
        { type: member.type, uid: member.uid, name },
      ];

      setMessage(nextValue);
      setMentionQuery(null);
      setActiveIndex(0);

      requestAnimationFrame(() => {
        const nextCaret = triggerStart + name.length + 2;
        input?.focus();
        input?.setSelectionRange?.(nextCaret, nextCaret);
      });
    },
    [message, setMessage],
  );

  const handleKeyDown = useCallback(
    (event: any) => {
      if (mentionQuery === null || suggestions.length === 0) return false;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((current) => (current + 1) % suggestions.length);
        return true;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((current) => (current - 1 + suggestions.length) % suggestions.length);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insertMention(suggestions[activeIndex]);
        return true;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMentionQuery(null);
        return true;
      }

      return false;
    },
    [mentionQuery, suggestions, activeIndex, insertMention],
  );

  return {
    ...composer,
    inputRef,
    mentionSuggestions: suggestions,
    mentionActiveIndex: activeIndex,
    isMentionOpen: mentionQuery !== null && suggestions.length > 0,
    handleChange,
    handleKeyDown,
    insertMention,
  };
};
